import {
  withEntities,
  withActiveId,
  selectAllEntities,
  selectActiveEntity,
  setEntities,
  getActiveId,
  setActiveId,
  hasEntity,
  upsertEntities
} from '@ngneat/elf-entities';
import { createStore } from '@ngneat/elf';
import {
  Animal,
  AnimalCount,
  Hub
} from '@tcc-mono/vandijk/shared/data-access/models';
import { inject, Injectable } from '@angular/core';
import { HubService, RideAnimalsWithoutPurchaseOrdersResponse } from '@tcc-mono/vandijk/shared/data-access/vandijk-api';
import { map, Observable, take, tap } from 'rxjs';
import { Response } from '@tcc-mono/shared/data-access';
import { LocalStorageService } from '@tcc-mono/shared/ui/theming';
import { Params } from '@angular/router';

const store = createStore(
  { name: 'hubs' },
  withEntities<Hub>(),
  withActiveId()
);

@Injectable({
  providedIn: 'root'
})
export class HubsRepository {

  private readonly _hubService: HubService = inject(HubService);
  private readonly _storage: LocalStorageService = inject(LocalStorageService);

  private _STORAGE_KEY = 'kvsHubId';
  public hubs$: Observable<Hub[]> = store.pipe(selectAllEntities());
  public activeHub$: Observable<Hub> = store.pipe(selectActiveEntity());

  public getHubs = (): Observable<Hub[]> => {
    return this._hubService.getAll().pipe(
      map(({ data }) => data),
      tap((hubs: Hub[]) => {
        store.update(setEntities(hubs));

        if (!this._storage.get(this._STORAGE_KEY) && hubs?.length > 0) {
          this._storage.set({ key: 'kvsHubId', value: hubs[0].id.toString() });
          this.setActiveHub(hubs[0].id);
        }

        if (this._storage.get(this._STORAGE_KEY) && hubs?.length > 0) {
          this.setActiveHub(+this._storage.get(this._STORAGE_KEY));
        }
      })
    );
  };

  public getAnimalCount = (
    id: number,
    params?: Params
  ): Observable<Response<AnimalCount>> => {
    return this._hubService.getChild<Response<AnimalCount>>(
      id,
      'animal_count',
      params
    );
  };

  public getRideAnimalsWithoutPurchaseOrder = (
    id: number,
    params?: Params
  ): Observable<Response<RideAnimalsWithoutPurchaseOrdersResponse>> => {
    return this._hubService.getChild<Response<RideAnimalsWithoutPurchaseOrdersResponse>>(
      id,
      'ride_animals_without_purchase_order',
      params
    );
  };

  public getAnimalsOnHub = (id?: number, params?: Params): Observable<Response<Animal[]>> => {
    const activeHub: string = this._storage.get(this._STORAGE_KEY);
    return this._hubService.getChild<Response<Animal[]>>(
      id ?? activeHub,
      'animals_on_hub',
      params
    );
  };

  public getAnimalOnHub = (animalId: number): Observable<Response<Animal>> => {
    const activeHub: string = this._storage.get(this._STORAGE_KEY);
    return this._hubService.getChild<Response<Animal>>(activeHub, `animals_on_hub/${animalId}`);
  }

  public reportAllRVO(activeHubId: number): Observable<unknown> {
    return this._hubService.postChild({}, `${activeHubId}/rvo/register_animals`);
  }

  public setActiveHub = (id: number): void => {
    this._storage.set({
      key: this._STORAGE_KEY,
      value: id.toString()
    });

    if (store.query(getActiveId) !== id) {
      store.update(setActiveId(id));

      if (!store.query(hasEntity(id))) {
        this._getHub(id).pipe(take(1)).subscribe();
      }
    }
  };

  private _getHub = (id: number): Observable<Hub> => {
    return this._hubService.get(id).pipe(
      map(({ data }) => data),
      tap((hub: Hub) => store.update(upsertEntities(hub)))
    );
  };
}
